import { PATH } from '@repo/constants';
import {
  logout,
  queryKeys,
  useAppType,
  useIsMobile,
  useUserDetailsQuery,
} from '@repo/lib';
import { AppType } from '@repo/types';
import { useQueryClient } from '@tanstack/react-query';
import SantaHatImage from '@ui/assets/images/santa-hat.png';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarMenu,
  SidebarMenuButton,
  Switch,
} from '@ui/components';
import { useSnowEffect } from '@ui/providers';
import { ChevronsUpDownIcon, LogOutIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const SantaHat = () => (
  <span className="pointer-events-none absolute -top-2.5 -left-3.5 block h-[30px] w-[34px] -rotate-6">
    <img
      alt=""
      aria-hidden
      className="h-full w-full scale-x-[-1] object-contain drop-shadow-sm"
      src={SantaHatImage}
    />
  </span>
);

type UserAvatarProps = {
  name?: string;
  fallbackName: string;
};

const UserAvatar = ({ name, fallbackName }: UserAvatarProps) => (
  <span className="relative inline-flex">
    <Avatar className="size-8 rounded-lg">
      <AvatarImage alt={name} />
      <AvatarFallback className="rounded-lg bg-primary text-white">
        {fallbackName}
      </AvatarFallback>
    </Avatar>
    <SantaHat />
  </span>
);

export const User = () => {
  const isMobile = useIsMobile();
  const appType = useAppType();
  const { enabled: isSnowEnabled, setEnabled: setSnowEnabled } = useSnowEffect();

  const { user } = useUserDetailsQuery();

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const fallbackName = useMemo(() => {
    const [firstName, lastName] = user?.name?.split(' ') ?? ['', ''];

    return `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
  }, [user?.name]);

  const handleLogout = () => {
    logout();
    queryClient.invalidateQueries({ queryKey: queryKeys.me.all });
    navigate(PATH.AUTH_LOGIN);
  };

  const showSnowToggle = appType === AppType.PORTAL;

  return (
    <SidebarMenu>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            className="overflow-visible data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            data-testid="sidebar-user-menu-trigger-btn"
            size="lg"
          >
            <UserAvatar
              fallbackName={fallbackName}
              name={user?.name}
            />
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">{user?.name}</span>
              <span className="truncate text-xs">{user?.email}</span>
            </div>
            <ChevronsUpDownIcon className="ml-auto size-4" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="z-[10000] w-[--radix-dropdown-menu-trigger-width] min-w-56 overflow-visible rounded-lg"
          side={isMobile ? 'bottom' : 'right'}
          sideOffset={4}
        >
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <UserAvatar
                fallbackName={fallbackName}
                name={user?.name}
              />
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{user?.name}</span>
                <span className="truncate text-xs">{user?.email}</span>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {showSnowToggle && (
            <DropdownMenuItem
              className="cursor-default focus:bg-transparent"
              onSelect={(event) => event.preventDefault()}
            >
              <div className="flex w-full items-center justify-between gap-3">
                <span className="text-sm font-semibold leading-tight">
                  Snow effect
                </span>
                <Switch
                  checked={isSnowEnabled}
                  onCheckedChange={setSnowEnabled}
                />
              </div>
            </DropdownMenuItem>
          )}
          {showSnowToggle && <DropdownMenuSeparator />}
          <DropdownMenuItem
            className="text-destructive"
            data-testid="sidebar-user-menu-logout-btn"
            onClick={handleLogout}
          >
            <LogOutIcon className="mr-2 size-4" />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenu>
  );
};
